import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Alert,
  AlertTitle,
  CircularProgress,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  Icon
} from '@mui/material';
import { useHealthData } from '../utils/hooks';

function HealthCheck() {
  const { healthData, loading, error, refetch } = useHealthData();

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'ok': return 'success';
      case 'fail': case 'error': return 'error';
      case 'degraded': return 'warning';
      default: return 'default';
    }
  };

  const getStatusLabel = (status) => {
    return status || 'Unknown';
  };

  return (
    <Box sx={{ p: 3 }}>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 700 }}>
                Health Check Service
              </Typography>
              <Typography variant="body1" color="text.secondary" paragraph>
                Monitor the health and status of all system services and dependencies.
              </Typography>
            </Box>
            <Button
              variant="outlined"
              onClick={refetch}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : <Icon>refresh</Icon>}
            >
              {loading ? 'Checking...' : 'Refresh'}
            </Button>
          </Box>
        </CardContent>
      </Card>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
            API Documentation
          </Typography>
          <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
            <Chip label="GET" color="success" size="small" sx={{ mr: 1 }} />
            <Typography component="span" sx={{ fontFamily: 'monospace' }}>
              /api/health
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" paragraph>
            Returns comprehensive health information for all services including status, uptime, and service-specific metrics.
          </Typography>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          <AlertTitle>Health Check Error</AlertTitle>
          {error}
        </Alert>
      )}

      {healthData && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
              System Overview
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
              <Chip
                label={`Overall: ${healthData.overall || 'Unknown'}`}
                color={getStatusColor(healthData.overall)}
                variant="outlined"
              />
              {healthData.timestamp && (
                <Chip
                  label={`Updated: ${new Date(healthData.timestamp).toLocaleString()}`}
                  variant="outlined"
                />
              )}
              {healthData.uptime && (
                <Chip
                  label={`Uptime: ${Math.floor(healthData.uptime / 3600)}h ${Math.floor((healthData.uptime % 3600) / 60)}m`}
                  variant="outlined"
                />
              )}
            </Box>
          </CardContent>
        </Card>
      )}
      
      {loading && !healthData ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Card>
          <CardContent>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
              Service Status
            </Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table sx={{ minWidth: 650 }} aria-label="health status table">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Service</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Message</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Details</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {healthData?.services && Object.entries(healthData.services).map(([serviceName, serviceData]) => {
                    const displayName = serviceName.charAt(0).toUpperCase() + serviceName.slice(1).replace(/([A-Z])/g, ' $1');
                    return (
                      <TableRow
                        key={serviceName}
                        sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                      >
                        <TableCell component="th" scope="row">
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {displayName}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={getStatusLabel(serviceData.status)}
                            color={getStatusColor(serviceData.status)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {serviceData.message || 'Service operational'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box>
                            {serviceData.bucket && (
                              <Typography variant="caption" display="block" color="text.secondary">
                                Bucket: {serviceData.bucket}
                              </Typography>
                            )}
                            {serviceData.data && typeof serviceData.data === 'object' && (
                              <Typography variant="caption" display="block" color="text.secondary">
                                Items: {Object.keys(serviceData.data).length}
                              </Typography>
                            )}
                            {serviceData.responseTime && (
                              <Typography variant="caption" display="block" color="text.secondary">
                                Response: {serviceData.responseTime}ms
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}
    </Box>
  );
}

export default HealthCheck;
